import { Card, Tag, Title, Text } from '@nimbus-ds/components'

// Search analytics (INS-1) — on-site search usage + terms + searcher conversion, from the
// window-aggregated `tnx_analytics.search_analytics`. null until snapshots carry it (re-agg).
export interface SearchAnalytics {
  search_sessions: number
  total_sessions: number
  usage_rate: number
  searcher_conversions: number
  searcher_conversion_rate: number
  non_searcher_sessions: number
  non_searcher_conversions: number
  non_searcher_conversion_rate: number
  empty_search_sessions: number
  top_terms: Array<{ term: string; sessions: number; conversions: number }>
}

export function SearchAnalyticsCard({ search }: { search?: SearchAnalytics | null }) {
  if (!search || search.total_sessions === 0) return null

  const usagePct = search.usage_rate * 100
  const sCR = search.searcher_conversion_rate * 100
  const nCR = search.non_searcher_conversion_rate * 100
  const lift = nCR > 0 ? ((sCR / nCR) - 1) * 100 : null
  const terms = (search.top_terms || []).slice(0, 10)

  return (
    <Card>
      <Card.Header>
        <Title as="h4">Buscador</Title>
      </Card.Header>
      <Card.Body>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Text fontSize="caption">Sesiones que buscan</Text>
              <Text fontWeight="bold" color="neutral-textHigh">
                {usagePct.toFixed(1)}%
              </Text>
              <Text fontSize="caption">{search.search_sessions.toLocaleString('es-ES')} sesiones</Text>
            </div>
            <div>
              <Text fontSize="caption">Conversión (buscan)</Text>
              <Text fontWeight="bold" color="neutral-textHigh">{sCR.toFixed(2)}%</Text>
            </div>
            <div>
              <Text fontSize="caption">Conversión (no buscan)</Text>
              <Text fontWeight="bold" color="neutral-textHigh">{nCR.toFixed(2)}%</Text>
            </div>
          </div>

          {lift != null && lift > 0 && (
            <Text fontSize="caption">
              Quienes buscan convierten {lift.toFixed(0)}% más — en parte porque quien busca ya sabe qué
              quiere (sesgo de intención), no solo por el buscador en sí.
            </Text>
          )}

          {terms.length > 0 && (
            <div>
              <Text fontSize="caption" fontWeight="medium">Términos más buscados</Text>
              <table className="mt-1 w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-neutral-500">
                    <th className="pb-1 font-medium">Término</th>
                    <th className="pb-1 text-right font-medium">Sesiones</th>
                    <th className="pb-1 text-right font-medium">Conv.</th>
                  </tr>
                </thead>
                <tbody>
                  {terms.map((t) => (
                    <tr key={t.term} className="border-t">
                      <td className="py-1">{t.term}</td>
                      <td className="py-1 text-right tabular-nums">{t.sessions.toLocaleString('es-ES')}</td>
                      <td className="py-1 text-right tabular-nums">{t.conversions.toLocaleString('es-ES')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {search.empty_search_sessions > 0 && (
            <div className="flex items-center gap-1">
              <Tag appearance="neutral">
                {search.empty_search_sessions.toLocaleString('es-ES')} búsquedas vacías
              </Tag>
              <Text fontSize="caption">— abren el buscador y no escriben (señal para el placeholder/CTA)</Text>
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

export default SearchAnalyticsCard
